import { Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import React from 'react';
import styled from 'styled-components';
import { ProductSize, ProductType } from '../../interfaces/Order';
import { ProductTemplateFulfillmentItem } from '../../interfaces/Stats';
import { CustomTable } from '../../styles/Global';
import { $primary } from '../../styles/Variables';

const { Text } = Typography;

const StyledTable = styled(CustomTable)`
  .ant-table-thead > tr > th {
    background-color: #f5f5f5;
    font-weight: 600;
  }

  .ant-table-tbody > tr > td {
    vertical-align: top;
  }

  .color-indicator {
    display: inline-block;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    margin-right: 8px;
    border: 1px solid #d9d9d9;
  }

  .revenue-cell {
    font-weight: 600;
    color: ${$primary};
  }

  .quantity-cell {
    font-weight: 500;
  }
`;

const NestedTable = styled(Table)`
  .ant-table-thead > tr > th {
    padding: 8px 12px;
    background-color: #fafafa;
  }

  .ant-table-tbody > tr > td {
    padding: 6px 12px;
  }
`;

const ProductTypeTag = styled(Tag)<{ $productType: ProductType }>`
  font-weight: 500;
  text-transform: uppercase;
  ${({ $productType }) => {
    switch ($productType) {
      case ProductType.TSHIRT:
        return 'background-color: #e6f7ff; border-color: #91d5ff; color: #0050b3;';
      default:
        return 'background-color: #f5f5f5; border-color: #d9d9d9; color: #595959;';
    }
  }}
`;

const SizeTag = styled(Tag)<{ $size: ProductSize }>`
  font-weight: 600;
  min-width: 40px;
  text-align: center;
  ${({ $size }) => {
    switch ($size) {
      case ProductSize.S:
        return 'background-color: #fff7e6; border-color: #ffd591; color: #d48806;';
      case ProductSize.M:
        return 'background-color: #f6ffed; border-color: #b7eb8f; color: #389e0d;';
      case ProductSize.L:
        return 'background-color: #e6f7ff; border-color: #91d5ff; color: #0050b3;';
      case ProductSize.XL:
        return 'background-color: #f9f0ff; border-color: #d3adf7; color: #722ed1;';
      case ProductSize.XXL:
        return 'background-color: #fff0f6; border-color: #ffadd2; color: #c41d7f;';
      case ProductSize.XXXL:
        return 'background-color: #f0f0f0; border-color: #bfbfbf; color: #434343;';
      default:
        return 'background-color: #f5f5f5; border-color: #d9d9d9; color: #595959;';
    }
  }}
`;

interface ProductTemplatesTableProps {
  data: ProductTemplateFulfillmentItem[];
  loading?: boolean;
}

const ProductTemplatesTable: React.FC<ProductTemplatesTableProps> = ({
  data,
  loading = false,
}) => {
  const formatCurrency = (amount: number) => {
    return `${amount.toLocaleString('vi-VN')} VNĐ`;
  };

  const variantColumns: ColumnsType<any> = [
    {
      title: 'Kích thước',
      dataIndex: 'size',
      key: 'size',
      width: 100,
      render: (size: ProductSize) => <SizeTag $size={size}>{size}</SizeTag>,
    },
    {
      title: 'Số lượng',
      dataIndex: 'quantityFulfilled',
      key: 'quantityFulfilled',
      width: 120,
      align: 'right',
      render: (quantity: number) => (
        <span className="quantity-cell">{quantity.toLocaleString()}</span>
      ),
    },
    {
      title: 'Doanh thu',
      dataIndex: 'revenue',
      key: 'revenue',
      width: 150,
      align: 'right',
      render: (revenue: number) => (
        <span className="revenue-cell">{formatCurrency(revenue)}</span>
      ),
    },
  ];

  const expandedRowRender = (record: any) => {
    return (
      <NestedTable
        columns={variantColumns}
        dataSource={record.variantBreakdown}
        pagination={false}
        rowKey="variantId"
        size="small"
        bordered
      />
    );
  };

  const columns: ColumnsType<any> = [
    {
      title: 'Tên mẫu',
      dataIndex: 'productTemplateName',
      key: 'productTemplateName',
      width: 200,
      render: (name: string) => (
        <Text strong style={{ fontSize: '14px' }}>
          {name}
        </Text>
      ),
    },
    {
      title: 'Loại sản phẩm',
      dataIndex: 'productType',
      key: 'productType',
      width: 140,
      render: (type: ProductType) => (
        <ProductTypeTag $productType={type}>{type}</ProductTypeTag>
      ),
    },
    {
      title: 'Màu sắc',
      dataIndex: 'color',
      key: 'color',
      width: 150,
      render: (color: string, record: any) => (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <span
            className="color-indicator"
            style={{ backgroundColor: record.colorHexCode || '#f0f0f0' }}
          />
          <Text>{color}</Text>
        </div>
      ),
    },
    {
      title: 'Form',
      dataIndex: 'form',
      key: 'form',
      width: 100,
      render: (form: string) => <Text>{form}</Text>,
    },
    {
      title: 'Tổng số lượng',
      dataIndex: 'totalQuantityFulfilled',
      key: 'totalQuantityFulfilled',
      width: 140,
      align: 'right',
      render: (quantity: number) => (
        <span className="quantity-cell">{quantity.toLocaleString()}</span>
      ),
    },
    {
      title: 'Tổng doanh thu',
      dataIndex: 'totalRevenue',
      key: 'totalRevenue',
      width: 160,
      align: 'right',
      render: (revenue: number) => (
        <span className="revenue-cell">{formatCurrency(revenue)}</span>
      ),
    },
  ];

  return (
    <StyledTable
      columns={columns}
      dataSource={data}
      rowKey="productTemplateId"
      loading={loading}
      pagination={{
        pageSize: 10,
        showSizeChanger: true,
        showQuickJumper: true,
        showTotal: (total, range) =>
          `${range[0]}-${range[1]} của ${total} mẫu sản phẩm`,
      }}
      expandable={{
        expandedRowRender,
        rowExpandable: (record) => record.variantBreakdown.length > 0,
      }}
      scroll={{ x: 900 }}
      bordered
    />
  );
};

export default ProductTemplatesTable;
